// useTabPrevention.tsx
import { useEffect } from 'react';

const useTabPrevention = (): void => {
  useEffect(() => {
    // Create a unique ID for this tab
    const tabId: string = Date.now().toString() + Math.random().toString().slice(2);

    // Initialize Broadcast Channel
    const channel: BroadcastChannel = new BroadcastChannel('tab-monitor');

    // Track if we should display the closure message
    let shouldShowClosureMessage: boolean = false;

    // Function to replace content with closure message
    const displayClosureMessage = (): void => {
      // Save current scrollTop to maintain position
      const scrollPos: number = window.scrollY;

      // Create a container to hold the current page state
      const container: HTMLDivElement = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.backgroundColor = 'white';
      container.style.zIndex = '99999';
      container.style.display = 'flex';
      container.style.flexDirection = 'column';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
      container.style.padding = '20px';
      container.style.boxSizing = 'border-box';

      // Create closure message
      const heading: HTMLHeadingElement = document.createElement('h1');
      heading.textContent = 'Multiple Tabs Detected';
      heading.style.fontFamily = 'sans-serif';
      heading.style.marginBottom = '20px';
      heading.style.textAlign = 'center';

      const message: HTMLParagraphElement = document.createElement('p');
      message.textContent = 'You already have one tab open. Please switch to it for a smoother experience ';
      message.style.fontFamily = 'sans-serif';
      message.style.textAlign = 'center';
      message.style.maxWidth = '450px';

      // Create a button to close this tab (will work if it's not the first tab)
    //   const closeButton: HTMLButtonElement = document.createElement('button');
    //   closeButton.textContent = 'Please go to that tab ';
    //   closeButton.style.marginTop = 'px';
    //   closeButton.style.padding = '3px 20px';
    //   closeButton.style.fontFamily = 'sans-serif';
    //   closeButton.style.cursor = 'pointer';
    //   closeButton.onclick = () => window.close();

      // Append elements
      container.appendChild(heading);
      container.appendChild(message);
    //   container.appendChild(closeButton);

      // Disable all event listeners on the page
      const disableAllListeners = (): void => {
        const elements: NodeListOf<Element> = document.querySelectorAll('*');
        elements.forEach(el => {
          (el as HTMLElement).style.pointerEvents = 'none';
        });
        container.style.pointerEvents = 'auto';
      };

      // Append to body and set page title
      document.body.appendChild(container);
      document.title = 'Tab Closed - Please Return to Original Tab';
      disableAllListeners();

      // Restore scroll position
      window.scrollTo(0, scrollPos);

      // Disable navigation
      window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
        return '';
      });
    };

    // Prevent keyboard shortcuts for new tabs
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.ctrlKey && (e.key === 't' || e.key === 'n')) ||
          (e.ctrlKey && e.shiftKey && e.key === 'n')) {
        e.preventDefault();
        e.stopPropagation();
        console.log('Prevented new tab keyboard shortcut');
      }
    };

    // Prevent link clicks that would open in new tabs
    const handleClick = (e: MouseEvent): void => {
      let target: Element | null = e.target as Element;
      while (target && target.tagName !== 'A') {
        target = target.parentElement;
      }

      if (target && ((target as HTMLAnchorElement).target === '_blank' ||
                     (e.ctrlKey || e.metaKey || e.button === 1))) {
        e.preventDefault();
        e.stopPropagation();

        if ((target as HTMLAnchorElement).href) {
          window.location.href = (target as HTMLAnchorElement).href;
        }
        console.log('Prevented new tab from link click');
      }
    };

    // Type definition for the message data
    type MessageData = {
      type: 'ping' | 'pong';
      tabId: string;
    };

    // Handle messages from other tabs
    const handleMessage = (e: MessageEvent<MessageData>): void => {
      if (shouldShowClosureMessage) return;

      if (e.data.type === 'ping') {
        // Another tab exists
        if (e.data.tabId !== tabId) {
          // Respond to let them know we exist
          channel.postMessage({ type: 'pong', tabId });
        }
      } else if (e.data.type === 'pong') {
        if (e.data.tabId !== tabId) {
          console.log('Existing tab detected, closing this one');
          shouldShowClosureMessage = true;

          // Try to close window (probably won't work for manually opened tabs)
          window.close();

          // If we're still here, show the closure message
          setTimeout(() => {
            if (shouldShowClosureMessage) {
              displayClosureMessage();
            }
          }, 100);
        }
      }
    };

    // Register event listeners
    channel.addEventListener('message', handleMessage);
    document.addEventListener('keydown', handleKeyDown as EventListener, true);
    document.addEventListener('click', handleClick as EventListener, true);
    document.addEventListener('auxclick', handleClick as EventListener, true); // For middle-click

    // Send initial ping to check for existing tabs
    channel.postMessage({ type: 'ping', tabId });

    // Periodically check for other tabs
    const intervalId: number = window.setInterval(() => {
      if (!shouldShowClosureMessage) {
        channel.postMessage({ type: 'ping', tabId });
      }
    }, 2000);

    // Handle tab visibility changes
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && !shouldShowClosureMessage) {
        channel.postMessage({ type: 'ping', tabId });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup when component unmounts
    return () => {
      channel.removeEventListener('message', handleMessage);
      document.removeEventListener('keydown', handleKeyDown as EventListener, true);
      document.removeEventListener('click', handleClick as EventListener, true);
      document.removeEventListener('auxclick', handleClick as EventListener, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
      channel.close();
    };
  }, []);
};

export default useTabPrevention;
